import { useParams, useSearchParams } from 'react-router';
import { useStoreProductDetailQuery } from '@/entities/product';
import {
  useGuestPlainQuoteRequestFormQuery,
  usePlainQuoteRequestFormQuery,
} from '@/entities/quote';
import { useAuthStore } from '@/entities/user';
import { routeForStatus } from '@/features/auth';
import {
  CUSTOMIZE_COLOR_PARAM,
  ONBOARDING_PATH,
  QUOTE_REQUEST_QTY_PARAM,
  SIZE_QUANTITIES_PARAM,
} from '@/shared/config';
import { useGuestRequestReceipt } from '@/widgets/guest-quote-request-form';
import { plainQuoteProductImageUrl } from './plain-quote-product-image-url';
import { parsePlainQuoteRequestParams } from './plain-quote-request-params';

/**
 * 일반 견적 요청 화면의 주소 해석·조회 묶음. 주소(:productId·?color·?qty)를 해석하고, 로그인 여부로 회원·비회원 폼 조회 중
 * 하나만 켠다(비회원은 접수한 뒤 다시 묻지 않는다). 상품 상세는 폼에 보일 사진(고른 색상 미리보기 → 대표 사진)에만 쓴다 — 못 받아도 폼은 그린다.
 */
export const usePlainQuoteRequest = () => {
  const { productId = '' } = useParams();
  const [searchParams] = useSearchParams();
  const params = parsePlainQuoteRequestParams(
    productId,
    searchParams.get(CUSTOMIZE_COLOR_PARAM),
    searchParams.get(QUOTE_REQUEST_QTY_PARAM),
    searchParams.get(SIZE_QUANTITIES_PARAM),
  );
  const isAuthenticated = useAuthStore((state) => state.isAuthenticated);
  // 인증 전 계정은 서버(activeProcedure)가 FORBIDDEN 으로 거절한다 — 다시 시도 대신 온보딩으로 보낸다.
  const accountStatus = useAuthStore((state) => state.user?.status);
  const onboardingHref =
    isAuthenticated &&
    accountStatus !== undefined &&
    routeForStatus(accountStatus) === ONBOARDING_PATH
      ? ONBOARDING_PATH
      : null;
  const { receipt, keepReceipt } = useGuestRequestReceipt();
  const formInput = {
    productId: params?.productId ?? '',
    colorOptionId: params?.colorOptionId ?? '',
  };
  const memberFormQuery = usePlainQuoteRequestFormQuery(formInput, {
    enabled: params !== null && isAuthenticated,
  });
  const guestFormQuery = useGuestPlainQuoteRequestFormQuery(formInput, {
    enabled: params !== null && !isAuthenticated && receipt === null,
  });
  const productQuery = useStoreProductDetailQuery(formInput.productId, {
    enabled: params !== null,
  });

  return {
    params,
    isAuthenticated,
    onboardingHref,
    receipt,
    keepReceipt,
    memberFormQuery,
    guestFormQuery,
    productImageUrl: plainQuoteProductImageUrl(productQuery.data, params?.colorOptionId ?? null),
  };
};
